import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CITY_MAP } from "./cities";
import { SITE } from "./config";
import Seo from "./Seo";
import { Phone, ArrowRight, Truck, Box, Recycle, CheckCircle2, MapPin, Clock } from "lucide-react";

const BENNE_SIZES = [
  { volume: "3 m³", desc: "petits travaux, débarras léger" },
  { volume: "5 m³", desc: "salle de bain, cuisine, déco" },
  { volume: "8 m³", desc: "rénovation appartement" },
  { volume: "10 m³", desc: "rénovation maison" },
  { volume: "15 m³", desc: "gros chantier ou démolition" },
  { volume: "20 m³", desc: "chantier industriel ou paysager" },
  { volume: "30 m³", desc: "déchets volumineux" },
  { volume: "40 m³", desc: "très gros volume non dense" },
];

export default function BennePage() {
  const { slug } = useParams();
  const city = CITY_MAP[slug];
  if (!city) return <Navigate to="/" replace />;

  const url = `https://fbenvironnement.fr/benne/${city.slug}`;
  const title = `Location de benne à ${city.name} — FB Environnement (${city.postalCode.slice(0, 2)})`;
  const description = `Bennes 3 à 40 m³ livrées à ${city.name} sous 48h. Gravats, plâtre, déchets verts, DIB, bois. Devis transparent en 2 minutes. Centre FB Environnement à ${city.distanceKm} km.`;

  return (
    <div>
      <Seo title={title} description={description} path={`/benne/${city.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fbenvironnement.fr/" },
            { "@type": "ListItem", position: 2, name: `Location de benne à ${city.name}`, item: url },
          ],
        })}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <nav className="text-sm mb-4 text-stone-200">
            <Link to="/" className="hover:text-[#9DCB2E]">Accueil</Link>
            <span className="mx-2 text-stone-400">/</span>
            <span className="text-white font-bold">Benne à {city.name}</span>
          </nav>
          <span className="inline-block bg-[#9DCB2E] text-[#0f3a18] font-black uppercase tracking-[0.15em] text-xs px-3 py-1.5 rounded-full">Pros & particuliers · {city.name}</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-5">
            Location de benne à {city.name}
          </h1>
          <p className="text-stone-100 text-lg mt-4 max-w-2xl">
            Du 3 m³ au 40 m³, FB Environnement vous livre une benne à {city.name} en 24-48h. Tarif transparent, retrait inclus, valorisation tracée — pas de mauvaise surprise.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 bg-[#9DCB2E] hover:bg-[#86b323] text-[#0f3a18] font-black px-5 py-3 rounded-full shadow-lg shadow-[#9DCB2E]/30">
              <Phone className="w-4 h-4" strokeWidth={3} /> {SITE.phone}
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0f3a18] font-bold px-5 py-3 rounded-full">
              Devis benne {city.name} <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading font-black text-3xl">Choisissez votre volume de benne</h2>
        <p className="text-stone-600 mt-2">Bennes disponibles pour livraison à {city.name} ({city.postalCode}).</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {BENNE_SIZES.map((b) => (
            <div key={b.volume} className="bg-white border border-stone-100 rounded-2xl p-5 hover:border-[#9DCB2E] transition">
              <Box className="w-7 h-7 text-[#1F6B2E] mb-3" strokeWidth={2.5} />
              <div className="font-heading font-black text-2xl">{b.volume}</div>
              <p className="text-sm text-stone-600 mt-1">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-stone-50 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading font-black text-3xl">Comment ça se passe à {city.name}</h2>
          <div className="grid md:grid-cols-4 gap-4 mt-8">
            {[
              { icon: Phone, t: "Appel ou devis", d: `Vous nous appelez, on cale en 2 min : adresse à ${city.name}, type de déchet, volume.` },
              { icon: Truck, t: "Livraison 24-48h", d: `On dépose la benne à l'adresse de votre chantier à ${city.name}.` },
              { icon: Box, t: "Vous remplissez", d: "À votre rythme, sur la durée convenue. Capacité respectée." },
              { icon: Recycle, t: "Récupération + tri", d: "On récupère, on pèse au centre, on certifie la valorisation." },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-stone-100 rounded-3xl p-5">
                <div className="w-11 h-11 rounded-2xl bg-[#1F6B2E] text-white grid place-items-center mb-3"><s.icon className="w-5 h-5" /></div>
                <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-1">Étape {i + 1}</div>
                <div className="font-heading font-black text-lg">{s.t}</div>
                <p className="text-sm text-stone-600 mt-1.5">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid md:grid-cols-3 gap-5">
          <Link to={`/gravats/${city.slug}`} className="bg-white border-2 border-stone-100 hover:border-[#9DCB2E] rounded-3xl p-5 group transition">
            <MapPin className="w-7 h-7 text-[#1F6B2E] mb-3" /><div className="font-heading font-black text-lg">Évacuation de gravats à {city.name}</div>
            <p className="text-sm text-stone-600 mt-1">Bennes adaptées aux gravats, plâtre, béton.</p>
          </Link>
          <Link to={`/tarifs/${city.slug}`} className="bg-white border-2 border-stone-100 hover:border-[#9DCB2E] rounded-3xl p-5 transition">
            <Clock className="w-7 h-7 text-[#1F6B2E] mb-3" /><div className="font-heading font-black text-lg">Tarifs benne à {city.name}</div>
            <p className="text-sm text-stone-600 mt-1">Prix transparents par volume et matière.</p>
          </Link>
          <Link to={`/recyclage/${city.slug}`} className="bg-white border-2 border-stone-100 hover:border-[#9DCB2E] rounded-3xl p-5 transition">
            <Recycle className="w-7 h-7 text-[#1F6B2E] mb-3" /><div className="font-heading font-black text-lg">Apporter mes déchets {city.name}</div>
            <p className="text-sm text-stone-600 mt-1">Notre centre de recyclage à Saint-Victoret.</p>
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="bg-gradient-to-br from-[#9DCB2E] to-[#7ba81f] rounded-3xl p-8 sm:p-12 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#0f3a18]">Une benne à {city.name}, c&apos;est par ici.</h2>
          <p className="text-[#0f3a18]/80 mt-3 max-w-xl mx-auto">Devis en 2 minutes, livraison sous 48h.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 bg-[#0f3a18] text-white font-bold px-6 py-3 rounded-full"><Phone className="w-4 h-4" /> {SITE.phone}</a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0f3a18] font-bold px-6 py-3 rounded-full">Devis en ligne <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
